import React, { useState, useEffect } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { Save, Home, Euro, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

const BasicInfoForm = () => {
  const { financeData, updateBasicInfo, toggleRentPaid } = useFinance();
  const [formData, setFormData] = useState({
    salary: '',
    currentMoney: '',
    rent: ''
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Sincronizar el estado local con el contexto cuando cambie financeData
  useEffect(() => {
    setFormData({
      salary: financeData.salary || '',
      currentMoney: financeData.currentMoney || '',
      rent: financeData.rent || ''
    });
  }, [financeData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveSuccess(false);

    const salary = parseFloat(formData.salary) || 0;
    const currentMoney = parseFloat(formData.currentMoney) || 0;
    const rent = parseFloat(formData.rent) || 0;

    await updateBasicInfo(salary, currentMoney, rent);
    setSaving(false);
    setSaveSuccess(true);

    // Ocultar mensaje de éxito después de 3 segundos
    setTimeout(() => {
      setSaveSuccess(false);
    }, 3000);
  };

  const handleRentToggle = async () => {
    await toggleRentPaid();
  };

  // Mostrar resumen cuando está colapsado
  const hasData = formData.salary || formData.currentMoney || formData.rent;

  return (
    <div className="card mb-6 md:mb-8">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 md:gap-3">
          <h2 className="section-title">
            <span>⚙️</span>
            <span>Configuración</span>
          </h2>
          <Euro className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
        </div>
        <div className="flex items-center gap-3">
          {!isExpanded && hasData && (
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
              <span>€{formData.salary || 0}/mes</span>
              <span>•</span>
              <span>€{formData.currentMoney || 0} actual</span>
            </div>
          )}
          <div className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </div>
        </div>
      </button>

      {/* Collapsible Content */}
      <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[1000px] opacity-100 mt-6' : 'max-h-0 opacity-0'
        }`}>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
            <div className="form-group">
              <label htmlFor="salary" className="form-label">
                💰 Salario Mensual (€)
              </label>
              <input
                type="number"
                id="salary"
                name="salary"
                value={formData.salary}
                onChange={handleInputChange}
                placeholder="Ej: 1600"
                step="0.01"
                className="input-field"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="currentMoney" className="form-label">
                💳 Dinero Actual (€)
              </label>
              <input
                type="number"
                id="currentMoney"
                name="currentMoney"
                value={formData.currentMoney}
                onChange={handleInputChange}
                placeholder="Ej: 500"
                step="0.01"
                className="input-field"
                required
              />
            </div>
          </div>

          <div className={`p-4 rounded-lg border-2 transition-all duration-300 ${financeData.rentPaid
              ? 'bg-green-50 border-green-200'
              : 'bg-gray-50 border-gray-200'
            }`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div className="form-group mb-0">
                <label htmlFor="rent" className="form-label">
                  <Home className="inline w-4 h-4 mr-1" />
                  🏠 Alquiler Mensual (€)
                </label>
                <input
                  type="number"
                  id="rent"
                  name="rent"
                  value={formData.rent}
                  onChange={handleInputChange}
                  placeholder="Ej: 600"
                  step="0.01"
                  className="input-field"
                  required
                />
              </div>

              <button
                type="button"
                onClick={handleRentToggle}
                className={`btn w-full sm:w-auto ${financeData.rentPaid
                    ? 'btn-success'
                    : 'btn-primary'
                  }`}
              >
                {financeData.rentPaid ? '↩ Marcar Pendiente' : '✅ Marcar Pagado'}
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary w-full sm:w-auto"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Guardando...' : '💾 Guardar'}
            </button>

            {saveSuccess && (
              <div className="flex items-center justify-center gap-2 text-green-600 animate-fade-in">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">¡Guardado!</span>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BasicInfoForm;